import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";

import { SharedApiService } from "./sharedApi.service";
import { IExecuteProcedureResult, IGetTripRequestParams, IHeadersWithPaymentsResultData, IProcedureError, ResultCode } from "../model/pos.api.model";
import { ProcedureNameEnum } from "src/app/enums/procedure-name.enum";
import { DbSchemeEnum } from "src/app/enums/db-scheme.enum";
import { IGetHeadersWithPaymentsParams } from "../model/sp-procedures-params.model";
import { VirtualProductType } from "src/app/enums/virtual-product-type.enum";
import { IReservationData } from "../model/get-trip-data.model";

@Injectable({
  providedIn: "root",
})
export class TransactionService {

  private _sharedApiService: SharedApiService = inject(SharedApiService);

  constructor(private http: HttpClient) { }

  /**
   * Gets header with payments for given virtual product types.
   * @param headerUid - The HeaderUID to search for
   * @param virtualProductTypes - Array of VirtualProductType to search for
   * @returns Promise with the first matching header result or null
   */
  async getHeaderWithPayments(headerUid: string, virtualProductTypes: VirtualProductType[]): Promise<IHeadersWithPaymentsResultData> {
        const data: IGetHeadersWithPaymentsParams = {
          VirtualProductTypes: virtualProductTypes,
          HeaderUID: headerUid,
          SelectOutput: true
        };
    
        const request = {
          ProcedureName: ProcedureNameEnum.GetHeadersWithPayments,
          DbScheme: DbSchemeEnum.Dert,
          Parameters: { ...data, }
        };
    
        const response: IExecuteProcedureResult<Array<IHeadersWithPaymentsResultData>> = await this._sharedApiService.executeProcedure(request);
        if (response.data != null && response.data.length > 0) {
            return response.data[0];
        }
        return null;
      }

  /**
   * Gets headers with payments for given reservation id and virtual product types.
   * Used to resolve HeaderUID for payment rows that don't include it.
   */
  async getHeadersWithPaymentsByReservationId(
    reservationId: string,
    virtualProductTypes: VirtualProductType[],
  ): Promise<IHeadersWithPaymentsResultData[]> {
    const data: IGetHeadersWithPaymentsParams = {
      VirtualProductTypes: virtualProductTypes,
      ReservationID: reservationId,
      SelectOutput: true,
    };

    const request = {
      ProcedureName: ProcedureNameEnum.GetHeadersWithPayments,
      DbScheme: DbSchemeEnum.Dert,
      Parameters: { ...data },
    };

    const response: IExecuteProcedureResult<Array<IHeadersWithPaymentsResultData>> =
      await this._sharedApiService.executeProcedure(request);

    if (!response) {
      return [];
    }

    if (response.result !== ResultCode.OK) {
      return [];
    }

    return response.data ?? [];
  }

  async getReservationData(
    reservationId: string,
    virtualProductTypes: VirtualProductType[],
  ): Promise<IReservationData | IProcedureError | null> {
    const data: IGetTripRequestParams = {
      ReservationId: reservationId,
      VirtualProductTypes: virtualProductTypes,
    };

    const request = {
      ProcedureName: ProcedureNameEnum.GetTripData,
      DbScheme: DbSchemeEnum.Dert,
      Parameters: { ...data },
    };

    const response: IExecuteProcedureResult<IReservationData | IProcedureError> =
      await this._sharedApiService.executeProcedure(request);

    if (!response || response.result !== ResultCode.OK || !response.data) {
      return null;
    }

    return response.data;
  }
}
